import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { 
  Users, 
  Edit3, 
  Save, 
  X, 
  Search,
  Filter,
  BarChart3,
  FileText,
  ArrowUpDown,
  UserCheck,
  UserPlus,
  FolderOpen
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import EmployeeExpediente from './EmployeeExpediente';
import logger from '../utils/logger';

const EmployeeManagement = () => {
  const { user } = useAuth();
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editData, setEditData] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterDept, setFilterDept] = useState('all');
  
  // Employee Profile Detail state
  const [selectedEmployeeId, setSelectedEmployeeId] = useState(null);
  const [profileDetailOpen, setProfileDetailOpen] = useState(false);

  // Read filter from URL params
  useEffect(() => {
    const filterFromUrl = searchParams.get('filter');
    if (filterFromUrl) {
      // Map URL filter values to internal filter values
      const filterMap = {
        'critical': 'critical',
        'not_started': 'not_started',
        'pending_evaluation': 'not_started',
        'in_progress': 'in_progress',
        'completed': 'completed'
      };
      const mappedFilter = filterMap[filterFromUrl] || 'all';
      setFilterStatus(mappedFilter);
    }
  }, [searchParams]);

  const openEmployeeProfile = (employeeId) => {
    setSelectedEmployeeId(employeeId);
    setProfileDetailOpen(true);
  };

  const closeEmployeeProfile = () => {
    setProfileDetailOpen(false);
    setSelectedEmployeeId(null);
  };

  useEffect(() => {
    if (user?.rol !== 'company_admin') {
      navigate('/dashboard');
      return;
    }
    loadEmployees();
  }, [user, navigate]);

  const loadEmployees = async () => {
    try {
      const response = await axios.get('/companies/employees-and-candidates');
      if (response.data.success) {
        setEmployees(response.data.data);
      }
    } catch (error) {
      logger.error('Error loading employees:', error);
      toast.error(
        language === 'es' 
          ? 'Error al cargar empleados' 
          : 'Error loading employees'
      );
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employee) => {
    setEditingId(employee.id);
    setEditData({ ...employee });
  };

  const handleSave = async (employeeId) => {
    try {
      const response = await axios.put(`/companies/employees/${employeeId}`, editData);
      if (response.data.success) {
        toast.success(t('employee_management.updated_success', 'Employee updated successfully'));
        setEditingId(null);
        loadEmployees();
      }
    } catch (error) {
      toast.error(
        language === 'es'
          ? 'Error al actualizar empleado'
          : 'Error updating employee'
      );
    }
  };

  const handleCancel = () => {
    setEditingId(null);
    setEditData({});
  };

  const getStatusBadge = (employee) => {
    if (employee.completed_assessments > 0) {
      return <Badge className="bg-green-100 text-green-800">
        {language === 'es' ? 'Completado' : 'Completed'}
      </Badge>;
    } else if (employee.total_assessments > 0) {
      return <Badge className="bg-yellow-100 text-yellow-800">
        {t('in_progress', 'En Progreso')}
      </Badge>;
    } else {
      return <Badge className="bg-gray-100 text-gray-800">
        {language === 'es' ? 'Sin Empezar' : 'Not Started'}
      </Badge>;
    }
  };

  const viewEmployeeReport = (employeeId) => {
    navigate(`/reports/employee/${employeeId}`);
  };

  // Filter employees
  const filteredEmployees = employees.filter(employee => {
    const matchesSearch = employee.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         employee.email.toLowerCase().includes(searchTerm.toLowerCase());
    
    const matchesStatus = filterStatus === 'all' || 
                         (filterStatus === 'completed' && employee.completed_assessments > 0) ||
                         (filterStatus === 'in_progress' && employee.total_assessments > 0 && employee.completed_assessments === 0) ||
                         (filterStatus === 'not_started' && employee.total_assessments === 0);
    
    const matchesDept = filterDept === 'all' || employee.departamento === filterDept;
    
    return matchesSearch && matchesStatus && matchesDept;
  });

  // Get unique departments
  const departments = [...new Set(employees.map(emp => emp.departamento).filter(Boolean))];

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      return new Date(dateString).toLocaleDateString(language === 'es' ? 'es-ES' : 'en-US');
    } catch (error) {
      return 'Invalid Date';
    }
  };

  const convertToEmployee = async (userId) => {
    try {
      const response = await axios.post(`/users/${userId}/convert-to-employee`);
      if (response.data.success) {
        toast.success(t('employee_management.converted_to_employee', 'Candidate converted to employee successfully'));
        loadEmployees(); // Reload the list
      } else {
        toast.error(response.data.message || t('employee_management.conversion_failed', 'Conversion failed'));
      }
    } catch (error) {
      logger.error('Error converting to employee:', error);
      toast.error(t('employee_management.conversion_error', 'Error during conversion'));
    }
  };

  const convertToCandidate = async (userId) => {
    try {
      const response = await axios.post(`/users/${userId}/convert-to-candidate`);
      if (response.data.success) {
        toast.success(t('employee_management.converted_to_candidate', 'Employee converted to candidate successfully'));
        loadEmployees(); // Reload the list
      } else {
        toast.error(response.data.message || t('employee_management.conversion_failed', 'Conversion failed'));
      }
    } catch (error) {
      logger.error('Error converting to candidate:', error);
      toast.error(t('employee_management.conversion_error', 'Error during conversion'));
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div>
              <h1 className="text-2xl font-bold text-gray-900">
                {language === 'es' ? 'Gestión de Empleados' : 'Employee Management'}
              </h1>
              <p className="text-sm text-gray-600">
                {language === 'es' 
                  ? 'Administra empleados y sus evaluaciones'
                  : 'Manage employees and their assessments'
                }
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <Button onClick={() => navigate('/dashboard')} variant="outline">
                {t('buttons.back_to_dashboard', 'Volver al Dashboard')}
              </Button>
              <Button onClick={() => navigate('/reports')} variant="outline">
                📊 {t('buttons.view_reports', 'Ver Reportes')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto py-6 px-4 sm:px-6 lg:px-8">
        {/* Statistics */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-6">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {t('employee_management.total_employees', 'Total Empleados')}
                  </p>
                  <p className="text-2xl font-bold text-gray-900">{employees.length}</p>
                </div>
                <Users className="h-8 w-8 text-blue-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {language === 'es' ? 'Evaluaciones Completadas' : 'Completed Assessments'}
                  </p>
                  <p className="text-2xl font-bold text-green-600">
                    {employees.filter(e => e.completed_assessments > 0).length}
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-green-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {t('in_progress', 'En Progreso')}
                  </p>
                  <p className="text-2xl font-bold text-yellow-600">
                    {employees.filter(e => e.total_assessments > 0 && e.completed_assessments === 0).length}
                  </p>
                </div>
                <BarChart3 className="h-8 w-8 text-yellow-600" />
              </div>
            </CardContent>
          </Card>
          
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">
                    {language === 'es' ? 'Sin Empezar' : 'Not Started'}
                  </p>
                  <p className="text-2xl font-bold text-gray-600">
                    {employees.filter(e => e.total_assessments === 0).length}
                  </p>
                </div>
                <Users className="h-8 w-8 text-gray-600" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card className="mb-6">
          <CardContent className="p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div>
                <Label>{language === 'es' ? 'Buscar' : 'Search'}</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder={language === 'es' ? 'Buscar por nombre o email...' : 'Search by name or email...'}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>
              
              <div>
                <Label>{t('status', 'Estado')}</Label>
                <Select value={filterStatus} onValueChange={setFilterStatus}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                    <SelectItem value="completed">{language === 'es' ? 'Completado' : 'Completed'}</SelectItem>
                    <SelectItem value="in_progress">{t('in_progress', 'En Progreso')}</SelectItem>
                    <SelectItem value="not_started">{language === 'es' ? 'Sin Empezar' : 'Not Started'}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label>{language === 'es' ? 'Departamento' : 'Department'}</Label>
                <Select value={filterDept} onValueChange={setFilterDept}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{language === 'es' ? 'Todos' : 'All'}</SelectItem>
                    {departments.map(dept => (
                      <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-end">
                <Button
                  variant="outline"
                  onClick={() => {
                    setSearchTerm('');
                    setFilterStatus('all');
                    setFilterDept('all');
                  }}
                >
                  <Filter className="h-4 w-4 mr-2" />
                  {language === 'es' ? 'Limpiar' : 'Clear'}
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Employee List */}
        <Card>
          <CardHeader>
            <CardTitle>
              {language === 'es' ? 'Lista de Empleados' : 'Employee List'}
            </CardTitle>
            <CardDescription>
              {language === 'es' 
                ? `Mostrando ${filteredEmployees.length} de ${employees.length} empleados`
                : `Showing ${filteredEmployees.length} of ${employees.length} employees`
              }
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {filteredEmployees.map((employee) => (
                <div key={employee.id} className="border rounded-lg p-4">
                  {editingId === employee.id ? (
                    // Edit Mode
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                      <div>
                        <Label>{language === 'es' ? 'Nombre' : 'Name'}</Label>
                        <Input
                          value={editData.nombre || ''}
                          onChange={(e) => setEditData({...editData, nombre: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>{language === 'es' ? 'Email' : 'Email'}</Label>
                        <Input
                          value={editData.email || ''}
                          onChange={(e) => setEditData({...editData, email: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>{language === 'es' ? 'Departamento' : 'Department'}</Label>
                        <Input
                          value={editData.departamento || ''}
                          onChange={(e) => setEditData({...editData, departamento: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>{language === 'es' ? 'Teléfono' : 'Phone'}</Label>
                        <Input
                          value={editData.telefono || ''}
                          onChange={(e) => setEditData({...editData, telefono: e.target.value})}
                        />
                      </div>
                      <div>
                        <Label>{t('status', 'Estado')}</Label>
                        <Select
                          value={editData.activo ? 'true' : 'false'}
                          onValueChange={(value) => setEditData({...editData, activo: value === 'true'})}
                        >
                          <SelectTrigger>
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="true">{language === 'es' ? 'Activo' : 'Active'}</SelectItem>
                            <SelectItem value="false">{language === 'es' ? 'Inactivo' : 'Inactive'}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="flex items-end space-x-2">
                        <Button size="sm" onClick={() => handleSave(employee.id)}>
                          <Save className="h-4 w-4 mr-2" />
                          {language === 'es' ? 'Guardar' : 'Save'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={handleCancel}>
                          <X className="h-4 w-4 mr-2" />
                          {language === 'es' ? 'Cancelar' : 'Cancel'}
                        </Button>
                      </div>
                    </div>
                  ) : (
                    // View Mode
                    <div className="flex justify-between items-start">
                      <div className="flex-1 grid grid-cols-1 md:grid-cols-3 gap-4">
                        <div>
                          <h3 className="font-medium text-gray-900">{employee.nombre}</h3>
                          <p className="text-sm text-gray-600">{employee.email}</p>
                          <p className="text-sm text-gray-500">{employee.departamento || 'Sin departamento'}</p>
                        </div>
                        
                        <div>
                          <p className="text-sm text-gray-600">
                            {language === 'es' ? 'Evaluaciones' : 'Assessments'}: {employee.completed_assessments}/{employee.total_assessments}
                          </p>
                          <p className="text-sm text-gray-600">
                            {language === 'es' ? 'Registro' : 'Registered'}: {formatDate(employee.created_at)}
                          </p>
                          {employee.last_completed && (
                            <p className="text-sm text-gray-600">
                              {language === 'es' ? 'Último completado' : 'Last completed'}: {formatDate(employee.last_completed)}
                            </p>
                          )}
                        </div>
                        
                        <div className="flex items-center space-x-2">
                          {getStatusBadge(employee)}
                          <Badge variant={employee.rol === 'employee' ? "default" : "secondary"}>
                            {employee.rol === 'employee' 
                              ? (language === 'es' ? 'Empleado' : 'Employee')
                              : (language === 'es' ? 'Candidato' : 'Candidate')
                            }
                          </Badge>
                          {/* Status badge removed - not needed */}
                        </div>
                      </div>
                      
                      <div className="flex items-center space-x-2 ml-4">
                        {/* Role Conversion Buttons */}
                        {employee.rol === 'candidate' ? (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => convertToEmployee(employee.id)}
                            title={t('employee_management.convert_to_employee', 'Convert to Employee')}
                          >
                            <UserCheck className="h-4 w-4 mr-2" />
                            {language === 'es' ? 'Hacer Empleado' : 'Make Employee'}
                          </Button>
                        ) : (
                          <Button 
                            size="sm" 
                            variant="outline" 
                            onClick={() => convertToCandidate(employee.id)}
                            title={t('employee_management.convert_to_candidate', 'Convert to Candidate')}
                          >
                            <UserPlus className="h-4 w-4 mr-2" />
                            {language === 'es' ? 'Hacer Candidato' : 'Make Candidate'}
                          </Button>
                        )}
                        
                        <Button size="sm" variant="outline" onClick={() => viewEmployeeReport(employee.id)}>
                          <FileText className="h-4 w-4 mr-2" />
                          {language === 'es' ? 'Reporte' : 'Report'}
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => openEmployeeProfile(employee.id)}
                          className="bg-teal-50 border-teal-200 text-teal-700 hover:bg-teal-100"
                        >
                          <FolderOpen className="h-4 w-4 mr-2" />
                          {language === 'es' ? 'Expediente' : 'Profile'}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleEdit(employee)}>
                          <Edit3 className="h-4 w-4 mr-2" />
                          {language === 'es' ? 'Editar' : 'Edit'}
                        </Button>
                      </div>
                    </div>
                  )}
                </div>
              ))}
              
              {filteredEmployees.length === 0 && (
                <div className="text-center py-8">
                  <Users className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">
                    {language === 'es' 
                      ? 'No se encontraron empleados con los filtros actuales'
                      : 'No employees found with current filters'
                    }
                  </p>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Employee Profile Detail Panel */}
      <EmployeeExpediente
        employeeId={selectedEmployeeId}
        isOpen={profileDetailOpen}
        onClose={closeEmployeeProfile}
        onUpdate={loadEmployees}
      />
    </div>
  );
};

export default EmployeeManagement;